import { HTTPError } from '@ixigo/fetch';
import type { ApiErrorResponse } from '@/api';

export const DEFAULT_ERROR_TITLE = 'Oops! Something went wrong';
export const DEFAULT_ERROR_DESCRIPTION =
	'Sorry! We are unable to process your request at the moment. Please try again in sometime.';

/**
 * Generic API call helper that handles try/catch and error formatting
 * Provides consistent error handling across all API calls
 *
 * @param apiCall - The async function that makes the API call
 * @param context - Optional context for logging (e.g., "fetchFareDetails", "submitBooking")
 * @returns Promise that resolves to either success data or standardized error
 */
export async function executeApiCall<TSuccess, TErrorType>(
	apiCall: () => Promise<TSuccess | ApiErrorResponse<TErrorType>>,
	context?: string, // will be used for logging
): Promise<TSuccess | ApiErrorResponse<TErrorType>> {
	try {
		const result = await apiCall();
		return result;
	} catch (error) {
		// Handle HTTP errors with structured response
		if (error instanceof HTTPError) {
			try {
				const { response } = error;
				const body = await response.clone().json();
				return {
					errors: body?.errors,
				};
			} catch {
				// If we can't parse the error response, use default HTTP error format
				return {
					errors: {
						title: DEFAULT_ERROR_TITLE,
						message: DEFAULT_ERROR_DESCRIPTION,
						type: 'GENERIC_ERROR' as const,
					},
				} as ApiErrorResponse<TErrorType>;
			}
		}

		// Handle non-HTTP errors (network issues, etc.)
		return {
			errors: {
				type: 'GENERIC_ERROR' as const,
				title: DEFAULT_ERROR_TITLE,
				message: DEFAULT_ERROR_DESCRIPTION,
			},
		} as ApiErrorResponse<TErrorType>;
	}
}
